import React, { useState, useEffect } from "react";
import { BaseDialog } from "../../shared/BaseDialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FormService, FormField } from "@shared/services/FormServices";
import { Info } from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface TitleCaseTransformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetField: string;
  fileData: string[];
  onApplyTransformation: (config: any) => void;
}

const TitleCaseTransformationDialog: React.FC<
  TitleCaseTransformationDialogProps
> = ({ isOpen, onClose, targetField, fileData, onApplyTransformation }) => {
  const [formData, setFormData] = useState({
    separators: [],
    customSeparators: "",
    preserveCapitals: false,
  });

  const [previewData, setPreviewData] = useState<{
    before: string[];
    after: string[];
  }>({
    before: [],
    after: [],
  });

  useEffect(() => {
    if (fileData) {
      const samples = fileData.filter((s) => s && s !== "...").slice(0, 2);
      setPreviewData({ before: samples, after: [] });
      updatePreview(
        { separators: [], customSeparators: "", preserveCapitals: false },
        samples
      );
    }
  }, [fileData]);

  const fields: FormField[] = [
    {
      name: "checkboxGroupHeader",
      label: "",
      type: "custom",
      className: "col-span-full",
      customRender: () => (
        <div>
          <span className="text-sm font-semibold text-gray-700">
            Word separators
          </span>
        </div>
      ),
    },
    {
      name: "separatorSpace",
      type: "checkbox",
      label: "Space",
      className: "col-span-1",
    },
    {
      name: "separatorHyphen",
      type: "checkbox",
      label: "Hyphen",
      className: "col-span-1",
    },
    {
      name: "separatorUnderscore",
      type: "checkbox",
      label: "Underscore",
      className: "col-span-1",
    },
    {
      name: "separatorCustom",
      type: "checkbox",
      label: "Custom",
      className: "col-span-1",
    },
    {
      name: "customSeparators",
      type: "text",
      label: "Custom separators",
      placeholder: "Enter custom separators",
      showWhen: (formValues) => formValues.separatorCustom === true,
      helperText: "Enter characters that should be treated as word separators",
      className: "col-span-1",
    },
    {
      name: "preserveCapitals",
      type: "checkbox",
      label: "Preserve existing capitals for acronyms/abbreviations",
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);
    updatePreview(newFormData, previewData.before);
  };

  const updatePreview = (data: any, samples: string[]) => {
    const results = samples.map((item) => {
      let result = item;

      // First, apply title case with space as separator
      result = item
        .toLowerCase()
        .replace(
          /(^|\s)(\w)/g,
          (match, sep, letter) => sep + letter.toUpperCase()
        );

      // Then replace spaces with selected separators
      if (data.separatorHyphen === true) {
        result = result.replace(/\s+/g, "-");
      } else if (data.separatorUnderscore === true) {
        result = result.replace(/\s+/g, "_");
      } else if (data.separatorCustom === true && data.customSeparators) {
        // Use the first custom separator character
        const customSep = data.customSeparators.charAt(0);
        result = result.replace(/\s+/g, customSep);
      }
      // If no separator selected or only space selected, keep spaces

      // Handle preserve capitals option
      if (data.preserveCapitals) {
        // If preserving capitals, use original item casing for already capitalized words
        const originalWords = item.split(/\s+/);
        const resultWords = result.split(/[-_\s]+/);

        resultWords.forEach((word, idx) => {
          if (
            originalWords[idx] &&
            originalWords[idx] === originalWords[idx].toUpperCase()
          ) {
            resultWords[idx] = originalWords[idx];
          }
        });

        // Rejoin with the appropriate separator
        if (data.separatorHyphen === true) {
          result = resultWords.join("-");
        } else if (data.separatorUnderscore === true) {
          result = resultWords.join("_");
        } else if (data.separatorCustom === true && data.customSeparators) {
          result = resultWords.join(data.customSeparators.charAt(0));
        } else {
          result = resultWords.join(" ");
        }
      }

      return result;
    });

    setPreviewData({ before: samples, after: results });
  };

  const handleApply = () => {
    const separators: string[] = [];
    if (
      formData.separators?.includes("space") ||
      formData.separators?.length === 0
    )
      separators.push(" ");
    if (formData.separators?.includes("hyphen")) separators.push("-");
    if (formData.separators?.includes("underscore")) separators.push("_");
    if (formData.separators?.includes("custom") && formData.customSeparators) {
      formData.customSeparators.split("").forEach((char: string) => {
        if (char && !separators.includes(char)) separators.push(char);
      });
    }
    if (separators.length === 0) separators.push(" ");

    onApplyTransformation({
      targetField: toSnakeCase(targetField),
      transformation: {
        type: "title_case",
        order: 1,
        config: {
          separators,
          ...(formData.preserveCapitals && { preserve_capitals: true }),
        },
      },
    });
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Title Case Configuration"
      className="max-w-4xl w-full max-h-[90vh] flex flex-col"
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Convert to Title Case:</strong> Format names, titles, and
            addresses properly. Configure word separators for optimal results.
          </div>
        </div>

        <DynamicForm
          fields={fields}
          form={form}
          config={{
            fields,
            layout: "vertical",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={handleFieldChange}
          gridColumns={1}
        />

        {/* Preview */}
        <div className="border rounded-lg p-4 bg-gray-50 relative">
          <h4 className="font-medium mb-3 text-gray-900">Preview</h4>
          <div className="grid grid-cols-2 gap-6 relative">
            {/* Before */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Before</p>
              <div className="space-y-2">
                {previewData.before.length > 0 ? (
                  previewData.before.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-800">{item}</span>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-400">
                      No data available
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Arrow */}
            <div className="flex items-center justify-center absolute left-1/2 transform -translate-x-1/2 top-1/2 -translate-y-1/2">
              <span className="text-gray-400 text-lg">→</span>
            </div>

            {/* After */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">After</p>
              <div className="space-y-2">
                {previewData.after.length > 0 ? (
                  previewData.after.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-800">{item}</span>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-400">
                      No preview available
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply Transformation</Button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default TitleCaseTransformationDialog;
